import React, { useEffect, useRef, useState, useCallback } from "react";
import { Users2, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import electric_buses from "../Assets/electric_buses.png";
import electric_cars from "../Assets/electric_cars.png";
import electric_home_charger from "../Assets/electric_home_charger.png";
import electric_tricycles from "../Assets/electric_tricycles.png";
import mini_electric_cars from "../Assets/mini_electric_cars.png";
import tricycle_white from "../Assets/tricycle_white.jpg";

/* ─── product data ───────────────────────────────────────────────────────── */
const products = [
  {
    id: 1,
    name: "Electric Buses",
    image: electric_buses,
    capacity: 12,
    tag: "Fleet",
    description:
      "Zero-emission, low-maintenance buses built for Nigerian roads and high-volume urban routes.",
    price: 26_000_000,
  },
  {
    id: 2,
    name: "Electric Tricycle",
    image: tricycle_white,
    capacity: 2,
    tag: "Commercial",
    description:
      "Efficient, eco-friendly urban transport — ideal for short trips and last-mile deliveries.",
    price: 2_000_000,
  },
  {
    id: 3,
    name: "Private Electric Car",
    image: electric_cars,
    capacity: 5,
    tag: "Personal",
    description:
      "Smooth, quiet, and fuel-free. Save on running costs without sacrificing comfort.",
    price: 12_000_000,
  },
  {
    id: 4,
    name: "Home EV Charger",
    image: electric_home_charger,
    capacity: null,
    tag: "Accessory",
    description:
      "Fast, reliable home charging — wake up every morning to a full battery.",
    price: 200_000,
  },
  {
    id: 5,
    name: "Electric Tricycles",
    image: electric_tricycles,
    capacity: 3,
    tag: "Commercial",
    description:
      "Designed for smooth rides, low maintenance, and cost-effective cargo mobility.",
    price: 7_000_000,
  },
  {
    id: 6,
    name: "Mini Electric Vehicle",
    image: mini_electric_cars,
    capacity: 2,
    tag: "Personal",
    description:
      "Compact, agile, and perfectly sized for urban commuting with minimal running costs.",
    price: 5_000_000,
  },
];

/* ─── price formatter ────────────────────────────────────────────────────── */
const fmt = (n) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(n);

/* ─── WhatsApp handler ───────────────────────────────────────────────────── */
const WHATSAPP_NUMBER = "2349116459123";

const openWhatsApp = (productName, price) => {
  const message = encodeURIComponent(
    `Hi Electromob! I'm interested in purchasing the *${productName}* (from ${fmt(price)}).\n\nPlease share more details.`
  );
  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
};

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function ProductsSlider() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);
  const timerRef = useRef(null);

  /* ── responsive visible count ─────────────────────────────────────────── */
  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 640) setVisibleCount(1);
      else if (window.innerWidth < 1024) setVisibleCount(2);
      else setVisibleCount(3);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const maxIndex = products.length - visibleCount;

  /* ── auto-play ────────────────────────────────────────────────────────── */
  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 3500);
  }, [maxIndex]);

  useEffect(() => {
    if (!paused) startTimer();
    return () => clearInterval(timerRef.current);
  }, [paused, startTimer]);

  /* ── navigation ───────────────────────────────────────────────────────── */
  const go = useCallback(
    (dir) => {
      setCurrent((prev) =>
        dir === "next"
          ? prev >= maxIndex
            ? 0
            : prev + 1
          : prev <= 0
          ? maxIndex
          : prev - 1
      );
      startTimer();
    },
    [maxIndex, startTimer]
  );

  const goTo = (index) => {
    setCurrent(index);
    startTimer();
  };

  /* ── slide offset ─────────────────────────────────────────────────────── */
  const slideWidth = 100 / visibleCount;
  const offset = current * slideWidth;

  return (
    <section
      className="bg-[#0e0e0e] py-20 px-4 md:px-10 lg:px-16"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* ── heading ───────────────────────────────────────────────────────── */}
      <div className="text-center mb-14">
        <span className="text-[#6ab615] uppercase tracking-widest text-xs font-semibold">
          Electromob
        </span>
        <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">
          Our Products
        </h2>
        <p className="text-white/50 mt-3 max-w-xl mx-auto text-sm leading-relaxed">
          Electric vehicles and charging solutions built for Nigerian roads,
          lifestyles, and budgets.
        </p>
      </div>

      {/* ── slider ────────────────────────────────────────────────────────── */}
      <div className="relative max-w-6xl mx-auto">
        {/* track */}
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${offset}%)` }}
          >
            {products.map((p) => (
              <div
                key={p.id}
                className="flex-shrink-0 px-3"
                style={{ width: `${slideWidth}%` }}
              >
                <ProductCard
                  product={p}
                  onBuy={() => openWhatsApp(p.name, p.price)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* arrows */}
        <NavArrow
          dir="left"
          onClick={() => go("prev")}
          className="-left-5 md:-left-12"
        />
        <NavArrow
          dir="right"
          onClick={() => go("next")}
          className="-right-5 md:-right-12"
        />
      </div>

      {/* ── dots ──────────────────────────────────────────────────────────── */}
      <div className="flex justify-center mt-8 gap-2">
        {Array.from({ length: maxIndex + 1 }).map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`transition-all duration-300 rounded-full
              ${
                i === current
                  ? "bg-[#6ab615] w-6 h-2"
                  : "bg-white/20 w-2 h-2 hover:bg-white/40"
              }`}
          />
        ))}
      </div>
    </section>
  );
}

/* ─── product card ───────────────────────────────────────────────────────── */
const ProductCard = ({ product, onBuy }) => {
  const { name, image, capacity, tag, description, price } = product;

  return (
    <div
      className="group bg-[#1a1a1a] rounded-2xl overflow-hidden border border-white/5
                  hover:border-[#6ab615]/40 transition-all duration-300
                  hover:shadow-[0_0_24px_rgba(106,182,21,0.12)]
                  flex flex-col h-full"
    >
      {/* image */}
      <div className="relative overflow-hidden">
        <img
          src={image}
          alt={name}
          className="w-full aspect-[4/3] object-cover transition-transform
                     duration-500 group-hover:scale-105"
        />
        {/* tag badge */}
        <span
          className="absolute top-3 left-3 bg-[#6ab615] text-white text-[10px]
                       font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full"
        >
          {tag}
        </span>
        {/* capacity badge */}
        {capacity !== null && (
          <span
            className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm
                         text-white text-[10px] font-medium px-2.5 py-1 rounded-full
                         flex items-center gap-1"
          >
            <Users2 className="w-3 h-3" />
            {capacity}
          </span>
        )}
      </div>

      {/* body */}
      <div className="flex flex-col flex-1 p-5 gap-3">
        <h3 className="text-white font-bold text-lg leading-snug">{name}</h3>
        <p className="text-white/50 text-sm leading-relaxed flex-1">
          {description}
        </p>

        {/* price + CTA */}
        <div className="pt-3 border-t border-white/5 flex items-center justify-between gap-3">
          <div>
            <p className="text-white/40 text-[10px] uppercase tracking-wider">
              From
            </p>
            <p className="text-[#6ab615] font-bold text-base">{fmt(price)}</p>
          </div>
          <button
            onClick={onBuy}
            className="flex items-center gap-2 bg-[#6ab615] hover:bg-[#5a9b12]
                       text-white text-sm font-semibold px-4 py-2.5 rounded-xl
                       transition-colors duration-200 shrink-0"
          >
            <Zap className="w-4 h-4" />
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── nav arrow ──────────────────────────────────────────────────────────── */
const NavArrow = ({ dir, onClick, className }) => (
  <button
    onClick={onClick}
    aria-label={dir === "left" ? "Previous slide" : "Next slide"}
    className={`absolute top-[45%] -translate-y-1/2 z-10 ${className}
                bg-[#1a1a1a] border border-white/10 hover:border-[#6ab615]/60
                hover:bg-[#6ab615]/10 text-white rounded-full p-2.5
                transition-all duration-200 shadow-lg`}
  >
    {dir === "left" ? (
      <ChevronLeft className="w-5 h-5" />
    ) : (
      <ChevronRight className="w-5 h-5" />
    )}
  </button>
);